import type { Device } from "@/types";
import type { LucideIcon } from "lucide-react";
import {
  BatteryCharging,
  BatteryFull,
  BatteryLow,
  BatteryMedium,
} from "lucide-react";
import { isReaderPaired } from "@/lib/readerLabel";

/** How long telemetry is considered fresh (admin devices view). */
export const TELEMETRY_FRESH_MS = 3 * 60 * 60 * 1000;

/** Stale-telemetry threshold for the in-use yellow warning beside the serial. */
export const IN_USE_WARNING_TELEMETRY_STALE_MS = 24 * 60 * 60 * 1000;

/** Tablet or reader battery at or below this triggers the in-use yellow warning. */
export const IN_USE_WARNING_LOW_BATTERY = 25;

export type TelemetryStatus = "gray" | "green" | "yellow" | "red";

export type SetupBadgeKind = "none" | "incomplete" | "revoked";

export type SetupMissingKind = "binding" | "pairing" | "assignment";

/** Minimal FreeKiosk /status shape the portal reads. */
export interface DeviceStatusPayload {
  success?: boolean;
  app_version?: string;
  data?: DeviceStatusData;
  timestamp?: number | string;
  /** Flat fallback when `data` is absent. */
  battery?: DeviceBatteryStatus;
  wifi?: DeviceWifiStatus;
  audio?: DeviceAudioStatus;
  storage?: DeviceUsageStatus;
  memory?: DeviceUsageStatus;
}

export interface DeviceBatteryStatus {
  level?: number;
  charging?: boolean;
}

export interface DeviceWifiStatus {
  signalLevel?: number;
}

export interface DeviceAudioStatus {
  volume?: number;
}

export interface DeviceUsageStatus {
  usedPercent?: number;
}

export interface DeviceStatusData {
  latitude?: number;
  longitude?: number;
  accuracy?: number;
  battery?: DeviceBatteryStatus;
  wifi?: DeviceWifiStatus;
  audio?: DeviceAudioStatus;
  storage?: DeviceUsageStatus;
  memory?: DeviceUsageStatus;
}

/** Minimal GPS / location JSON the portal reads. */
export interface DeviceLocationPayload {
  success?: boolean;
  app_version?: string;
  data?: {
    latitude?: number;
    longitude?: number;
    accuracy?: number;
  };
  /** Flat fallback when `data` is absent. */
  latitude?: number;
  longitude?: number;
  accuracy?: number;
}

export type DeviceStatusInput = Pick<
  Device,
  | "software_id"
  | "last_login_at"
  | "last_device_report_at"
  | "device_status_payload"
  | "device_location_payload"
  | "house_id"
  | "is_revoked"
> & {
  reader?: Device["reader"];
};

export type DeviceCoordinates = {
  latitude: number;
  longitude: number;
  accuracyMeters: number | null;
};

export function asRecord(value: unknown): Record<string, unknown> | null {
  if (typeof value !== "object" || value === null) {
    return null;
  }
  return value as Record<string, unknown>;
}

/** Battery Lucide icon from charge level (0–100) and optional charging flag. */
export function getBatteryIcon(
  level: number | null,
  charging: boolean | null = null,
): LucideIcon {
  if (charging === true) {
    return BatteryCharging;
  }
  if (level === null || level > 70) {
    return BatteryFull;
  }
  if (level > 40) {
    return BatteryMedium;
  }
  return BatteryLow;
}

function isPresentPayload(value: unknown): boolean {
  return value !== null && value !== undefined;
}

/** Prefer nested `.data`, otherwise the payload root (FreeKiosk shapes vary). */
export function getDeviceStatusData(
  payload: DeviceStatusPayload | null | undefined,
): Record<string, unknown> | null {
  const payloadRecord = asRecord(payload);
  if (!payloadRecord) {
    return null;
  }
  return asRecord(payloadRecord.data) ?? payloadRecord;
}

export function getDeviceStatusTimestamp(
  payload: DeviceStatusPayload | null | undefined,
): Date | null {
  const payloadRecord = asRecord(payload);
  if (!payloadRecord) {
    return null;
  }

  const timestamp = payloadRecord.timestamp;
  const numericTimestamp =
    typeof timestamp === "number"
      ? timestamp
      : typeof timestamp === "string"
        ? Number(timestamp)
        : Number.NaN;
  if (!Number.isFinite(numericTimestamp)) {
    return null;
  }

  const milliseconds =
    numericTimestamp < 1_000_000_000_000
      ? numericTimestamp * 1000
      : numericTimestamp;
  const date = new Date(milliseconds);
  return Number.isNaN(date.getTime()) ? null : date;
}

function readTelemetryRecord(
  data: Record<string, unknown> | null,
  key: string,
): Record<string, unknown> | null {
  if (!data) {
    return null;
  }
  return asRecord(data[key]);
}

export function readTelemetryNumber(
  data: Record<string, unknown> | null,
  section: string,
  key: string,
): number | null {
  const value = readTelemetryRecord(data, section)?.[key];
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

export function readTelemetryBoolean(
  data: Record<string, unknown> | null,
  section: string,
  key: string,
): boolean | null {
  const value = readTelemetryRecord(data, section)?.[key];
  return typeof value === "boolean" ? value : null;
}

export function readDeviceCoordinates(
  payload: DeviceLocationPayload | null | undefined,
): DeviceCoordinates | null {
  const payloadRecord = asRecord(payload);
  if (!payloadRecord || payloadRecord.success === false) {
    return null;
  }

  const dataRecord = asRecord(payloadRecord.data) ?? payloadRecord;
  const latitude = dataRecord.latitude;
  const longitude = dataRecord.longitude;
  const accuracy = dataRecord.accuracy;

  if (
    typeof latitude !== "number" ||
    typeof longitude !== "number" ||
    !Number.isFinite(latitude) ||
    !Number.isFinite(longitude) ||
    latitude < -90 ||
    latitude > 90 ||
    longitude < -180 ||
    longitude > 180
  ) {
    return null;
  }

  return {
    latitude,
    longitude,
    accuracyMeters:
      typeof accuracy === "number" && Number.isFinite(accuracy) && accuracy > 0
        ? accuracy
        : null,
  };
}

/** FreeKiosk /status payload was stored on the last report. */
export function hasDeviceStatusPayload(
  payload: DeviceStatusPayload | null | undefined,
): boolean {
  if (!isPresentPayload(payload)) {
    return false;
  }
  const record = asRecord(payload);
  if (!record) {
    return true;
  }
  if (Object.keys(record).length === 0) {
    return false;
  }
  if (record.success === false) {
    return false;
  }
  return true;
}

/** Device GPS payload includes coordinates. */
export function hasGpsPayload(
  payload: DeviceLocationPayload | null | undefined,
): boolean {
  if (!isPresentPayload(payload)) {
    return false;
  }
  const record = asRecord(payload);
  if (!record) {
    return false;
  }
  if (record.success === false) {
    return false;
  }
  const data = asRecord(record.data) ?? record;
  return (
    typeof data.latitude === "number" && typeof data.longitude === "number"
  );
}

export function isTelemetryComplete(device: DeviceStatusInput): boolean {
  return (
    hasDeviceStatusPayload(device.device_status_payload) &&
    hasGpsPayload(device.device_location_payload)
  );
}

export function isTelemetryRecent(
  lastReportAt: string | null | undefined,
  nowMs: number = Date.now(),
): boolean {
  if (lastReportAt == null || lastReportAt === "") {
    return false;
  }
  const reportedAt = new Date(lastReportAt).getTime();
  if (Number.isNaN(reportedAt)) {
    return false;
  }
  return nowMs - reportedAt <= TELEMETRY_FRESH_MS;
}

export function getTelemetryStatus(
  device: DeviceStatusInput,
  nowMs: number = Date.now(),
): TelemetryStatus {
  const softwareId = device.software_id?.trim();
  if (!softwareId || device.last_login_at == null) {
    return "gray";
  }
  if (!isTelemetryRecent(device.last_device_report_at, nowMs)) {
    return "red";
  }
  if (isTelemetryComplete(device)) {
    return "green";
  }
  return "yellow";
}

export function telemetryStatusDotClass(status: TelemetryStatus): string {
  switch (status) {
    case "gray":
      return "bg-surface-400";
    case "green":
      return "bg-success-500";
    case "yellow":
      return "bg-warning-400";
    case "red":
      return "bg-error-500";
  }
}

export function isDeviceReady(device: DeviceStatusInput): boolean {
  return (
    device.is_revoked !== true &&
    Boolean(device.software_id?.trim()) &&
    Boolean(device.house_id) &&
    isReaderPaired(device.reader)
  );
}

export function getSetupBadgeKind(device: DeviceStatusInput): SetupBadgeKind {
  if (device.is_revoked === true) {
    return "revoked";
  }
  if (isDeviceReady(device)) {
    return "none";
  }
  return "incomplete";
}

export type DeviceAppVersionLabels = {
  unknown: string;
  legacy: string;
};

/** Table/filter sort treats the legacy `<=0.4.0` label as this semver. */
const LEGACY_APP_VERSION_SORT_AS = "0.3.0";

function readAppVersionFromPayload(
  payload: DeviceStatusPayload | DeviceLocationPayload | null | undefined,
): string | null {
  const version = asRecord(payload)?.app_version;
  if (typeof version !== "string") {
    return null;
  }
  const trimmed = version.trim();
  return trimmed.length > 0 ? trimmed : null;
}

/** Pre-release channel order: a < b < rc < final. */
const APP_VERSION_PRE_CHANNEL: Record<string, number> = {
  a: 1,
  alpha: 1,
  b: 2,
  beta: 2,
  rc: 3,
};
const APP_VERSION_FINAL_CHANNEL = 4;
/** Leaves room for channel (1–4) * 1000 + pre-release number (0–999). */
const APP_VERSION_PRE_UNIT = 10_000;

const APP_VERSION_CORE_RE = /^(\d+)\.(\d+)(?:\.(\d+))?(?:\.(\d+))?$/;
const APP_VERSION_PRE_RE =
  /^(\d+)\.(\d+)\.(\d+)[-.]?(a|b|rc|alpha|beta)(\d+)$/i;

/**
 * Numeric rank for app versions as stored on devices
 * (e.g. 0.5.10, 0.6.1, 0.6.0rc7, 0.6.0a2).
 * Pre-releases sort before the matching final: a < b < rc < release.
 * Returns null for non-semver labels or unparseable strings.
 */
export function appVersionSortRank(version: string): number | null {
  const trimmed = version.trim();
  const pre = APP_VERSION_PRE_RE.exec(trimmed);
  if (pre) {
    const major = Number(pre[1]);
    const minor = Number(pre[2]);
    const patch = Number(pre[3]);
    const channelKey = pre[4].toLowerCase();
    const channel = APP_VERSION_PRE_CHANNEL[channelKey];
    const preNumber = Number(pre[5]);
    if (channel == null || !Number.isFinite(preNumber)) {
      return null;
    }
    const core = major * 1_000_000 + minor * 1_000 + patch;
    return core * APP_VERSION_PRE_UNIT + channel * 1_000 + preNumber;
  }

  const coreMatch = APP_VERSION_CORE_RE.exec(trimmed);
  if (!coreMatch) {
    return null;
  }
  const major = Number(coreMatch[1]);
  const minor = Number(coreMatch[2]);
  const patch = coreMatch[3] != null ? Number(coreMatch[3]) : 0;
  const build = coreMatch[4] != null ? Number(coreMatch[4]) : 0;
  const core = major * 1_000_000 + minor * 1_000 + patch + build / 1_000;
  return core * APP_VERSION_PRE_UNIT + APP_VERSION_FINAL_CHANNEL * 1_000;
}

function appVersionLabelRank(
  version: string,
  labels: DeviceAppVersionLabels,
): number | null {
  if (version === labels.unknown) {
    return null;
  }
  if (version === labels.legacy) {
    return appVersionSortRank(LEGACY_APP_VERSION_SORT_AS);
  }
  return appVersionSortRank(version);
}

export function compareAppVersionLabels(
  a: string,
  b: string,
  labels: DeviceAppVersionLabels,
): number {
  if (a === labels.unknown && b === labels.unknown) {
    return 0;
  }
  if (a === labels.unknown) {
    return 1;
  }
  if (b === labels.unknown) {
    return -1;
  }
  const rankA = appVersionLabelRank(a, labels);
  const rankB = appVersionLabelRank(b, labels);
  if (rankA != null && rankB != null) {
    // Newest first in filter dropdowns.
    return rankB - rankA;
  }
  return b.localeCompare(a, undefined, { numeric: true });
}

/** Resolve app version for device tables from telemetry payloads. */
export function formatDeviceAppVersion(
  device: Pick<
    Device,
    | "last_device_report_at"
    | "device_status_payload"
    | "device_location_payload"
  >,
  labels: DeviceAppVersionLabels,
): string {
  const fromLocation = readAppVersionFromPayload(device.device_location_payload);
  if (fromLocation) {
    return fromLocation;
  }
  const fromStatus = readAppVersionFromPayload(device.device_status_payload);
  if (fromStatus) {
    return fromStatus;
  }
  if (device.last_device_report_at == null || device.last_device_report_at === "") {
    return labels.unknown;
  }
  return labels.legacy;
}

/**
 * Sort key for device table app-version column.
 * Legacy sorts as 0.3.0; unknown is null so DataTable always places it last.
 */
export function deviceAppVersionSortValue(
  device: Pick<
    Device,
    | "last_device_report_at"
    | "device_status_payload"
    | "device_location_payload"
  >,
  labels: DeviceAppVersionLabels,
): number | null {
  return appVersionLabelRank(formatDeviceAppVersion(device, labels), labels);
}

export function getSetupMissingItems(
  device: DeviceStatusInput,
): SetupMissingKind[] {
  const missing: SetupMissingKind[] = [];
  if (!device.software_id?.trim()) {
    missing.push("binding");
  }
  if (!isReaderPaired(device.reader)) {
    missing.push("pairing");
  }
  if (!device.house_id) {
    missing.push("assignment");
  }
  return missing;
}

export type InUseWarningKind =
  | "staleTelemetry"
  | "outdatedAppVersion"
  | "deviceLowBattery"
  | "readerLowBattery"
  | "missingReader"
  | "missingStructure"
  | "missingRoom";

/** Display / filter order for every in-use attention check. */
export const IN_USE_WARNING_KIND_ORDER: readonly InUseWarningKind[] = [
  "staleTelemetry",
  "outdatedAppVersion",
  "deviceLowBattery",
  "readerLowBattery",
  "missingReader",
  "missingStructure",
  "missingRoom",
] as const;

export type InUseWarningInput = Pick<
  Device,
  | "is_shelved"
  | "last_device_report_at"
  | "house_id"
  | "room_name"
  | "device_status_payload"
  | "device_location_payload"
> & {
  reader?: Device["reader"];
};

export type InUseCheckStatus = "ok" | "issue" | "unknown";

/**
 * Minimum kiosk app version that is not treated as an attention problem.
 * Versions strictly below this (and legacy reports without a version) flag
 * `outdatedAppVersion`. Unset / blank / unparseable disables the check.
 */
export function getKioskMinAppVersion(): string | null {
  const raw = import.meta.env.VITE_KIOSK_MIN_APP_VERSION?.trim();
  if (!raw) {
    return null;
  }
  return appVersionSortRank(raw) != null ? raw : null;
}

/**
 * Compare a device's reported app version against the configured minimum.
 * Legacy telemetry (report present, no `app_version`) counts as outdated.
 */
export function getAppVersionAttentionStatus(
  device: Pick<
    Device,
    | "last_device_report_at"
    | "device_status_payload"
    | "device_location_payload"
  >,
  minVersion: string | null = getKioskMinAppVersion(),
): InUseCheckStatus {
  if (minVersion == null) {
    return "ok";
  }
  const minRank = appVersionSortRank(minVersion);
  if (minRank == null) {
    return "ok";
  }

  const reported =
    readAppVersionFromPayload(device.device_location_payload) ??
    readAppVersionFromPayload(device.device_status_payload);
  if (reported) {
    const rank = appVersionSortRank(reported);
    if (rank == null) {
      return "unknown";
    }
    return rank < minRank ? "issue" : "ok";
  }

  if (device.last_device_report_at == null || device.last_device_report_at === "") {
    return "unknown";
  }
  // Pre-app_version telemetry is treated as older than any configured minimum.
  return "issue";
}

function isReportWithinMs(
  lastReportAt: string | null | undefined,
  maxAgeMs: number,
  nowMs: number,
): boolean {
  if (lastReportAt == null || lastReportAt === "") {
    return false;
  }
  const reportedAt = new Date(lastReportAt).getTime();
  if (Number.isNaN(reportedAt)) {
    return false;
  }
  return nowMs - reportedAt <= maxAgeMs;
}

function batteryCheckStatus(level: number | null): InUseCheckStatus {
  if (level === null) {
    return "unknown";
  }
  return level <= IN_USE_WARNING_LOW_BATTERY ? "issue" : "ok";
}

/**
 * Per-check status for the in-use attention popover.
 * Battery checks are `unknown` when no reading is available.
 */
export function getInUseCheckStatuses(
  device: InUseWarningInput,
  nowMs: number = Date.now(),
): Record<InUseWarningKind, InUseCheckStatus> {
  const deviceBattery = readTelemetryNumber(
    getDeviceStatusData(device.device_status_payload),
    "battery",
    "level",
  );
  const readerBatteryRaw = device.reader?.device_status_snapshot?.battery_level;
  const readerBattery =
    typeof readerBatteryRaw === "number" && Number.isFinite(readerBatteryRaw)
      ? readerBatteryRaw
      : null;

  return {
    staleTelemetry: isReportWithinMs(
      device.last_device_report_at,
      IN_USE_WARNING_TELEMETRY_STALE_MS,
      nowMs,
    )
      ? "ok"
      : "issue",
    outdatedAppVersion: getAppVersionAttentionStatus(device),
    deviceLowBattery: batteryCheckStatus(deviceBattery),
    readerLowBattery: batteryCheckStatus(readerBattery),
    missingReader: isReaderPaired(device.reader) ? "ok" : "issue",
    missingStructure: device.house_id ? "ok" : "issue",
    missingRoom: device.room_name?.trim() ? "ok" : "issue",
  };
}

/**
 * Yellow warning reasons for tablets marked in use.
 * Empty when the device is shelved (warehouse) or has no issues.
 */
export function getInUseWarningKinds(
  device: InUseWarningInput,
  nowMs: number = Date.now(),
): InUseWarningKind[] {
  if (device.is_shelved === true) {
    return [];
  }

  const statuses = getInUseCheckStatuses(device, nowMs);
  return IN_USE_WARNING_KIND_ORDER.filter(
    (kind) => statuses[kind] === "issue",
  );
}

export type SetupCompletenessFilter =
  | "all"
  | "complete"
  | "incomplete"
  | "revoked";

export type ShelvedFilter = "all" | "in_service" | "shelved";

export function matchesSetupCompletenessFilter(
  device: DeviceStatusInput & InUseWarningInput,
  filter: SetupCompletenessFilter,
  nowMs: number = Date.now(),
): boolean {
  switch (filter) {
    case "all":
      return true;
    case "complete":
      return isDeviceReady(device);
    case "incomplete":
      // “Requires attention” in the UI: same causes as the serial warning.
      return getInUseWarningKinds(device, nowMs).length > 0;
    case "revoked":
      return device.is_revoked === true;
  }
}

export function matchesShelvedFilter(
  device: Pick<Device, "is_shelved">,
  filter: ShelvedFilter,
): boolean {
  switch (filter) {
    case "all":
      return true;
    case "in_service":
      return device.is_shelved !== true;
    case "shelved":
      return device.is_shelved === true;
  }
}

export function matchesIncompleteMissingFilters(
  device: InUseWarningInput,
  filters: readonly InUseWarningKind[],
  nowMs: number = Date.now(),
): boolean {
  if (filters.length === 0) {
    return true;
  }
  const kinds = getInUseWarningKinds(device, nowMs);
  return filters.some((kind) => kinds.includes(kind));
}

export type TelemetryStatusFilter = "all" | TelemetryStatus;

export function matchesTelemetryStatusFilter(
  device: DeviceStatusInput,
  filter: TelemetryStatusFilter,
  nowMs: number = Date.now(),
): boolean {
  if (filter === "all") {
    return true;
  }
  return getTelemetryStatus(device, nowMs) === filter;
}

export function matchesAppVersionFilters(
  device: Pick<
    Device,
    | "last_device_report_at"
    | "device_status_payload"
    | "device_location_payload"
  >,
  selectedVersions: readonly string[],
  labels: DeviceAppVersionLabels,
): boolean {
  if (selectedVersions.length === 0) {
    return true;
  }
  return selectedVersions.includes(formatDeviceAppVersion(device, labels));
}
